import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { MapPin, User, ClipboardList, Shield, LogIn, ArrowLeft, Home, Bell } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
        <MapPin className="h-6 w-6" strokeWidth={2.6} />
      </span>
      <span className="font-display text-lg font-extrabold leading-tight text-slate-800 sm:text-xl">
        Prestadores <span className="text-orange-500">Online</span>
      </span>
    </Link>
  );
}

const linkCls = ({ isActive }) =>
  `flex min-h-[48px] items-center gap-2 rounded-xl px-2.5 sm:px-3 text-base font-semibold transition-colors ${
    isActive ? "bg-orange-50 text-orange-700" : "text-slate-700 hover:bg-slate-100"
  }`;

function BellLink() {
  const { data } = useQuery({ queryKey: ["notif-count"], queryFn: () => api.get("/notificacoes/nao-lidas").then((r) => r.data), refetchInterval: 20000 });
  const n = data?.unread || 0;
  return (
    <NavLink to="/notificacoes" className={linkCls} aria-label={`Notificações${n ? ` (${n} não lidas)` : ""}`} data-testid="nav-notifications">
      <span className="relative">
        <Bell className="h-6 w-6 text-orange-600" strokeWidth={2.4} />
        {n > 0 && (
          <span className="absolute -right-2.5 -top-2.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-xs font-extrabold text-white ring-2 ring-white" data-testid="nav-notifications-badge">
            {n > 99 ? "99+" : n}
          </span>
        )}
      </span>
      <span className="hidden sm:inline">Avisos</span>
    </NavLink>
  );
}

export default function Header() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-2.5 sm:px-6">
        <Logo />
        {user ? (
          <nav className="flex items-center gap-0.5 sm:gap-1">
            <BellLink />
            {user.is_admin && (
              <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
                <Shield className="h-5 w-5" /> <span className="hidden sm:inline">Admin</span>
              </NavLink>
            )}
            <NavLink to="/meus-pedidos" className={linkCls} data-testid="nav-my-requests">
              <ClipboardList className="h-5 w-5" /> <span className="hidden sm:inline">Meus pedidos</span>
            </NavLink>
            <NavLink to="/perfil" className={linkCls} data-testid="nav-profile">
              <User className="h-5 w-5" /> <span className="hidden sm:inline">Perfil</span>
            </NavLink>
          </nav>
        ) : (
          <button onClick={() => navigate("/entrar")} className="po-btn-primary min-h-[48px] px-5 text-base" data-testid="nav-login">
            <LogIn className="h-5 w-5" /> Entrar
          </button>
        )}
      </div>
      {pathname !== "/" && (
        <div className="mx-auto max-w-5xl px-4 pb-2.5 sm:px-6">
          <Link to="/" className="inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-orange-500 bg-orange-50 px-5 text-lg font-bold text-orange-700 transition-colors hover:bg-orange-500 hover:text-white" data-testid="home-button">
            <ArrowLeft className="h-5 w-5" /> <Home className="h-6 w-6" /> Início
          </Link>
        </div>
      )}
    </header>
  );
}
